import type { JSX } from 'react'
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Send } from 'lucide-react'
import type { AlertChannel, AlertKind, AlertRuleDto, AlertTestResultDto } from '@/lib/dto'
import { ALERT_CHANNELS, ALERT_KINDS } from '@/lib/dto'
import { api } from '@/lib/api'
import { qk } from '@/lib/query-keys'
import { DEFAULT_TOAST_MS, useToastStore } from '@/stores/toasts'
import { useDraft } from '@/ui/hooks/useDraft'
import { Button } from '@/ui/primitives/Button'
import { Dialog } from '@/ui/primitives/Dialog'
import { NumberField, TextField } from '@/ui/primitives/Field'
import { Select } from '@/ui/primitives/Select'
import { Switch } from '@/ui/primitives/Switch'
import { fieldErrorsOf, problemText } from '../shared/problem'
import {
  ALERT_CHANNEL_META,
  ALERT_KIND_META,
  MAX_BOT_DOWN_MINUTES,
  MAX_PATTERN_LENGTH,
  MIN_BOT_DOWN_MINUTES,
  alertFormOf,
  createRequestOf,
  emptyAlertForm,
  updateRequestOf,
  validateAlertForm,
  type AlertFormState
} from './alert-model'
import { AlertTestResult } from './AlertTestResult'

/**
 * Диалог правила алерта: одна форма на создание и на правку (`rule === null` —
 * создаём).
 *
 * Прогрессивное раскрытие: видны только те поля, которые имеют смысл для
 * выбранного вида. У `authFailed` и `degraded` настроек нет вовсе — вместо
 * пустого блока «Параметры» там стоит строка о том, когда правило сработает.
 *
 * Адрес доставки при правке спрятан за кнопкой «Изменить адрес». Это не
 * украшение: панель видит только маску (`•••7890`), и поле, предзаполненное
 * маской, рано или поздно уехало бы в PATCH как настоящий адрес.
 *
 * Поля держит `useDraft`: список правил перечитывается после каждой мутации, и
 * синхронизация формы по объекту правила стирала бы уже введённое название.
 */

export interface AlertRuleDialogProps {
  open: boolean
  rule: AlertRuleDto | null
  onClose: () => void
}

export function AlertRuleDialog({ open, rule, onClose }: AlertRuleDialogProps): JSX.Element {
  const queryClient = useQueryClient()
  const push = useToastStore(state => state.push)

  const seed = open ? (rule?.id ?? 'new') : null
  const draft = useDraft(rule === null ? emptyAlertForm() : alertFormOf(rule), seed)
  const form = draft.value
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<AlertTestResultDto | null>(null)

  // Сброс по метке формы, а не по объекту правила: фоновый рефетч не должен
  // стирать итог только что сделанной пробной доставки.
  useEffect(() => {
    setErrors({})
    setFormError(null)
    setTestResult(null)
  }, [seed])

  const patch = (next: Partial<AlertFormState>): void => draft.patch(next)

  const validate = (): boolean => {
    const found = validateAlertForm(form)
    setErrors(found)
    return Object.keys(found).length === 0
  }

  const onFailure = (error: unknown): void => {
    const fields = fieldErrorsOf(error)
    if (Object.keys(fields).length > 0) setErrors(fields)
    setFormError(problemText(error))
  }

  const done = (message: string): void => {
    void queryClient.invalidateQueries({ queryKey: qk.alerts.root })
    push({ message, tone: 'success', timeoutMs: DEFAULT_TOAST_MS })
    onClose()
  }

  const create = useMutation({
    mutationFn: () => api.alerts.create(createRequestOf(form)),
    onSuccess: created => done(`Правило «${created.name}» создано`),
    onError: onFailure
  })

  const save = useMutation({
    mutationFn: () => {
      if (rule === null) throw new Error('нет правила для правки')
      return api.alerts.update(rule.id, updateRequestOf(form, rule))
    },
    onSuccess: updated => done(`Правило «${updated.name}» сохранено`),
    onError: onFailure
  })

  /**
   * Пробная доставка. Результат остаётся в форме, а не улетает тостом: ответ
   * приходит с кодом 200 даже когда доставка не удалась, и «webhook вернул 404»
   * нужно читать рядом с полем адреса, а не ловить за три секунды в углу экрана.
   */
  const test = useMutation({
    mutationFn: () => {
      if (rule === null) throw new Error('правило ещё не сохранено')
      return api.alerts.test(rule.id)
    },
    onMutate: () => setTestResult(null),
    onSuccess: result => setTestResult(result),
    // Здесь ошибка — это уже сбой самого запроса (403, 429, сеть), а не отказ
    // транспорта: её место в общем баннере формы.
    onError: error => setFormError(problemText(error))
  })

  const changeKind = (kind: AlertKind): void => patch({ kind })

  /**
   * Смена канала при правке обязана открыть поле адреса: chat id не является
   * URL и наоборот, а прежнее значение панель прислать не может. Сервер иначе
   * ответит 409 — лучше объяснить это формой, чем ошибкой после «Сохранить».
   */
  const changeChannel = (channel: AlertChannel): void => {
    if (rule !== null && channel !== rule.channel) patch({ channel, targetEditing: true })
    else if (rule !== null && channel === rule.channel) patch({ channel, targetEditing: false })
    else patch({ channel })
  }

  const busy = create.isPending || save.isPending
  const channelMeta = ALERT_CHANNEL_META[form.channel]

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={rule === null ? 'Новое правило алерта' : `Правило «${rule.name}»`}
      note="Правило описывает, о чём и куда панель напишет, когда с флотом что-то не так"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          {rule === null ? null : (
            <Button variant="secondary" loading={test.isPending} onClick={() => test.mutate()}>
              <Send aria-hidden className="size-4" />
              Проверить доставку
            </Button>
          )}
          <Button
            variant="primary"
            loading={busy}
            onClick={() => {
              setFormError(null)
              if (!validate()) return
              if (rule === null) create.mutate()
              else save.mutate()
            }}
          >
            {rule === null ? 'Создать' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <TextField
          label="Название правила"
          value={form.name}
          required
          autoFocus
          maxLength={64}
          error={errors.name ?? null}
          onBlur={() => {
            if (form.name !== '') validate()
          }}
          onChange={event => patch({ name: event.target.value })}
        />

        <Select
          label="Когда срабатывает"
          value={form.kind}
          onValueChange={value => changeKind(value as AlertKind)}
          options={ALERT_KINDS.map(kind => ({
            value: kind,
            label: ALERT_KIND_META[kind].label
          }))}
          hint={ALERT_KIND_META[form.kind].when}
        />

        {/* Параметры вида. Раскрываются только там, где они есть: блок
            «Параметры» с одной надписью «нет настроек» — это шум. */}
        {form.kind === 'botDown' ? (
          <NumberField
            label="Сколько минут терпеть"
            value={form.minutes}
            min={MIN_BOT_DOWN_MINUTES}
            max={MAX_BOT_DOWN_MINUTES}
            required
            hint="Проверка идёт раз в минуту, поэтому меньше минуты смысла не имеет. Максимум — сутки."
            error={errors['config.minutes'] ?? null}
            onBlur={() => validate()}
            onChange={event => patch({ minutes: event.target.value })}
          />
        ) : null}

        {form.kind === 'kickMatch' ? (
          <div className="grid gap-4 sm:grid-cols-[1fr_7rem]">
            <TextField
              label="Регулярное выражение"
              mono
              required
              placeholder="restart|maintenance"
              maxLength={MAX_PATTERN_LENGTH}
              value={form.pattern}
              hint="Сравнивается с текстом кика. Шаблоны вида (a+)+ сервер отклонит: они вешают проверку."
              error={errors['config.pattern'] ?? null}
              onBlur={() => validate()}
              onChange={event => patch({ pattern: event.target.value })}
            />
            <TextField
              label="Флаги"
              mono
              maxLength={4}
              placeholder="i"
              value={form.flags}
              hint="i, m, s, u"
              onBlur={() => validate()}
              onChange={event => patch({ flags: event.target.value })}
            />
          </div>
        ) : null}

        <div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
          <Select
            label="Куда доставлять"
            value={form.channel}
            onValueChange={value => changeChannel(value as AlertChannel)}
            options={ALERT_CHANNELS.map(channel => ({
              value: channel,
              label: ALERT_CHANNEL_META[channel].label
            }))}
            hint={
              form.channel === 'telegram'
                ? 'Сообщение отправит бот Telegram, настроенный в окружении панели'
                : 'POST с JSON на ваш адрес: Slack, Discord, n8n'
            }
          />

          {form.targetEditing ? (
            <TextField
              label={channelMeta.targetLabel}
              mono
              required={form.channel === 'webhook'}
              placeholder={
                form.channel === 'telegram'
                  ? '-1001234567890'
                  : 'https://hooks.slack.com/services/…'
              }
              value={form.target}
              hint={
                form.channel === 'telegram'
                  ? 'Пусто — писать в чат по умолчанию из настроек сервера'
                  : 'Адрес виден только при вводе: дальше он хранится зашифрованным'
              }
              error={errors.target ?? null}
              onBlur={() => validate()}
              onChange={event => patch({ target: event.target.value })}
            />
          ) : (
            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="text-xs font-medium text-ink-muted">{channelMeta.targetLabel}</span>
              <div className="flex min-h-9 items-center justify-between gap-2 rounded-sm border border-line bg-panel-2 px-3">
                <span className="min-w-0 truncate font-mono text-base text-ink-muted">
                  {rule?.targetMasked ?? '—'}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => patch({ targetEditing: true, target: '' })}
                >
                  Изменить адрес
                </Button>
              </div>
              <p className="max-w-prose text-2xs text-ink-muted">
                Панель хранит адрес зашифрованным и показывает только маску, поэтому изменить его
                можно, лишь введя заново.
              </p>
            </div>
          )}
        </div>

        <Switch
          label="Правило включено"
          checked={form.enabled}
          onCheckedChange={enabled => patch({ enabled })}
          hint="Выключенное правило остаётся в списке, но не срабатывает и никуда не пишет"
        />

        {testResult === null ? null : <AlertTestResult result={testResult} />}

        {formError === null ? null : (
          <p
            role="alert"
            className="rounded-sm border border-fail/40 bg-fail/12 px-3 py-2 text-base text-fail"
          >
            {formError}
          </p>
        )}
      </div>
    </Dialog>
  )
}
